import { Box, IconButton, Typography } from '@mui/material';
import { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import { DateTime } from 'luxon';
import i18next from 'i18next';
import { ChevronRightIconCalendar as ChevronRight } from '../Icons/ChevronRightIcon';
import ChevronLeft from '../Icons/ChevronLeftIcon';

function CalendarHeader(props: PickersCalendarHeaderProps<DateTime>) {
  const { currentMonth, onMonthChange, disablePast, disableFuture, maxDate, minDate } = props;

  /**
   * Handler for previous month
   */
  const handlePreviousMonth = () => {
    onMonthChange(currentMonth.minus({ month: 1 }), 'right');
  };

  /**
   * Handler for next month
   */
  const handleNextMonth = () => {
    onMonthChange(currentMonth.plus({ month: 1 }), 'left');
  };

  /**
   * Helper function to check if previous month should be disabled
   */
  const isPreviousDisabled = (month: DateTime): boolean => {
    const previousMonth = month.minus({ month: 1 });
    const today = DateTime.now().startOf('day');

    // Check against disablePast
    if (disablePast && previousMonth.endOf('month') < today) {
      return true;
    }

    // Check against minDate
    if (minDate && previousMonth.endOf('month') < minDate.startOf('day')) {
      return true;
    }

    return false;
  };

  /**
   * Helper function to check if next month should be disabled
   */
  const isNextDisabled = (month: DateTime): boolean => {
    const nextMonth = month.plus({ month: 1 });
    const today = DateTime.now().startOf('day');

    if (disableFuture && nextMonth.startOf('month') > today) {
      return true;
    }
    if (maxDate && nextMonth.startOf('month') > maxDate.endOf('day')) {
      return true;
    }

    return false;
  };

  const isPreviousMonthDisabled = isPreviousDisabled(currentMonth);
  const isNextMonthDisabled = isNextDisabled(currentMonth);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem 0.75rem',
        height: '3.5rem',
      }}
    >
      <IconButton
        onClick={handlePreviousMonth}
        disabled={isPreviousMonthDisabled}
        disableRipple
        sx={{
          '& svg': {
            opacity: isPreviousMonthDisabled ? 0.5 : 1,
          },
        }}
      >
        <ChevronLeft stroke={isPreviousMonthDisabled ? '#606060' : '#333'} />
      </IconButton>

      <Typography variant="h6" component="div" sx={{ fontWeight: 500 }}>
        {currentMonth.setLocale(i18next.language).toFormat('LLLL yyyy')}
      </Typography>

      <IconButton
        onClick={handleNextMonth}
        disabled={isNextMonthDisabled}
        disableRipple
        sx={{
          '& svg': {
            opacity: isNextMonthDisabled ? 0.5 : 1,
          },
        }}
      >
        <ChevronRight stroke={isNextMonthDisabled ? '#606060' : '#333'} />
      </IconButton>
    </Box>
  );
}

export default CalendarHeader;
